import {
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  OnDestroy,
  signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActionDialog, SiActionDialogService } from '@siemens/element-ng/action-modal';
import { SiCardModule } from '@siemens/element-ng/card';
import { SiModalService, ModalRef } from '@siemens/element-ng/modal';
import { SiToastNotificationService } from '@siemens/element-ng/toast-notification';
import { take } from 'rxjs';
import { STANDARD_MODAL_CONFIG } from 'src/app/main/shared/const';
import { BrouterMapComponent } from 'src/app/main/shared/brouter-map/brouter-map.component';
import {
  ExportToUiPayload,
  MapViewport,
  Waypoint
} from 'src/app/main/shared/brouter-map/models/brouter-map.model';
import { createWaypoint } from 'src/app/main/shared/brouter-map/services/route-editor.service';

import { EventService } from '../../../../shared/services/event.service';
import { MapPlannerComponent, PlannedRoutePoint } from './map-planner/map-planner.component';

@Component({
  selector: 'app-map',
  imports: [SiCardModule, TranslateModule, MapPlannerComponent, BrouterMapComponent],
  templateUrl: './map.component.html',
  styleUrl: './map.component.scss'
})
export class MapComponent implements OnDestroy {
  readonly isOpened = model.required<boolean>();
  readonly initialWaypoints = input<Waypoint[]>([]);

  private readonly eventService = inject(EventService);
  private readonly siModal = inject(SiActionDialogService);
  private readonly modalService = inject(SiModalService);
  private readonly toastNotificationService = inject(SiToastNotificationService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly mapModalTemplate = viewChild<TemplateRef<unknown>>('mapModal');
  private modalRef: ModalRef<unknown> | null = null;

  readonly isFullscreen = signal(false);
  /** Route handed to the map. Replaced wholesale whenever the planner generates one. */
  readonly routeWaypoints = signal<Waypoint[]>([]);
  readonly mapViewport = signal<MapViewport | null>(null);

  /** Restores the pre-tour route once the guided tour finishes. */
  private tourRouteSnapshot: Waypoint[] | null = null;
  private hasTourRouteSnapshot = false;

  constructor() {
    effect(() => {
      const waypoints = this.initialWaypoints();
      if (waypoints.length === 0) {
        return;
      }
      this.routeWaypoints.set([...waypoints]);
      this.mapViewport.set(null);
    });

    // Opening is driven by the parent through `isOpened`, so the modal follows it.
    effect(() => {
      if (this.isOpened()) {
        this.openModal();
      } else {
        this.modalRef?.hide();
      }
    });

    // Fullscreen swaps a class on the live dialog, which Element owns. The configured
    // modal class lands on `.modal-dialog` itself, so that is what gets toggled.
    effect(() => {
      const dialog = document.querySelector('.map-modal');
      dialog?.classList.toggle('map-modal--fullscreen', this.isFullscreen());
      document.body.style.overflow = this.isFullscreen() ? 'hidden' : '';
    });

    this.eventService.loadTourMapRouteEvent$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.hasTourRouteSnapshot) {
          this.tourRouteSnapshot = this.routeWaypoints();
          this.hasTourRouteSnapshot = true;
        }
      });

    this.eventService.clearTourMapRouteEvent$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.hasTourRouteSnapshot) {
          this.routeWaypoints.set(this.tourRouteSnapshot ?? []);
          this.tourRouteSnapshot = null;
          this.hasTourRouteSnapshot = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.modalRef?.hide();
    document.body.style.overflow = '';
  }

  private openModal(): void {
    const template = this.mapModalTemplate();
    if (!template || this.modalRef) {
      return;
    }

    this.modalRef = this.modalService.show(template, {
      ...STANDARD_MODAL_CONFIG,
      class: 'map-modal modal-dialog-centered'
    });

    // Centralised cleanup for every dismiss path: button, ESC and backdrop.
    this.modalRef.hidden.pipe(take(1)).subscribe(() => {
      this.modalRef = null;
      this.isFullscreen.set(false);
      document.body.style.overflow = '';
      this.isOpened.set(false);
    });
  }

  closeModal(): void {
    this.modalRef?.hide();
    this.isOpened.set(false);
  }

  onRouteGenerated(points: PlannedRoutePoint[]): void {
    this.routeWaypoints.set(points.map(point => createWaypoint(point, point.name)));
    this.mapViewport.set(null);
  }

  onRouteCleared(): void {
    this.routeWaypoints.set([]);
  }

  onRouteChanged(waypoints: Waypoint[]): void {
    this.routeWaypoints.set([...waypoints]);
  }

  onViewportChanged(viewport: MapViewport): void {
    this.mapViewport.set(viewport);
  }

  toggleFullscreen(): void {
    this.isFullscreen.update(v => !v);
  }

  toggleIsOpened(): void {
    this.isFullscreen.set(false);
    this.isOpened.update(value => !value);
  }

  onRoutingError(message: string): void {
    this.toastNotificationService.showToastNotification({
      state: 'danger',
      title: this.translateService.instant('BROUTER_MAP.ROUTING_FAILED'),
      message
    });
  }

  /** Confirms before discarding the drawn route, mirroring the legacy clear dialog. */
  onClearRequested(map: BrouterMapComponent): void {
    if (map.routeEditor.waypoints().length === 0) {
      return;
    }

    const dialog: ActionDialog = {
      type: 'confirmation',
      heading: this.translateService.instant('BROUTER_MAP.CLEAR_ROUTE'),
      message: this.translateService.instant('BROUTER_MAP.CLEAR_ROUTE_CONFIRM')
    };

    this.siModal.showActionDialog(dialog).subscribe(result => {
      if (result === 'confirm') {
        map.routeEditor.clear();
        this.routeWaypoints.set([]);
      }
    });
  }

  /**
   * Hands the drawn route to the track table.
   *
   * Replaces the legacy `postMessage` bridge from `Export_to_Ui.js`. The payload shape is
   * fixed by the backend's `POST /BRouter_interface/track` contract.
   */
  onExportRequested(map: BrouterMapComponent): void {
    const waypoints = map.routeEditor.waypoints();

    if (waypoints.length < 2) {
      this.showNotEnoughStationsToast();
      return;
    }

    const dialog: ActionDialog = {
      type: 'confirmation',
      heading: this.translateService.instant('MAP.OVERRIDE_TRACK_HEADING'),
      message: this.translateService.instant('MAP.OVERRIDE_TRACK_MESSAGE')
    };

    this.siModal.showActionDialog(dialog).subscribe(result => {
      if (result !== 'confirm') {
        return;
      }

      this.toggleIsOpened();
      this.toastNotificationService.showToastNotification({
        state: 'info',
        title: this.translateService.instant('MAP.TRACK_LOADING_HEADING'),
        message: this.translateService.instant('MAP.TRACK_LOADING_MESSAGE')
      });

      const payload: ExportToUiPayload = {
        array_lonlats: waypoints.map(({ lat, lng }) => ({ lat, lng })),
        profile: map.routeEditor.options().profile
      };
      this.eventService.openTrackFromMap(payload);
    });
  }

  private showNotEnoughStationsToast(): void {
    const toast = this.toastNotificationService.showToastNotification({
      state: 'danger',
      title: this.translateService.instant('MAP.NOT_ENOUGH_STATIONS_HEADING'),
      message: this.translateService.instant('MAP.NOT_ENOUGH_STATIONS_MESSAGE')
    });
    toast.hidden?.subscribe();
  }
}
